/*
 * Disney bags gallery (snippets/product-gallery-disney.liquid).
 *
 * - Below 1000px the photos are a swipe row: the thumbnail strip follows the swipe and a
 *   thumbnail tap slides to its photo. From 1000px the same list is a CSS grid.
 * - Clicking a photo opens the fullscreen viewer (arrows, swipe, arrow keys, Esc, and a thumbnail
 *   strip under the photo that follows the current slide). In the viewer a photo zooms: double-tap or
 *   pinch on touch screens, click with a mouse, then drag to look around.
 * - On the theme's variant:changed event, photos grouped by alt text for another option value
 *   hide (same rule as ProductGallery.variantHasChanged in theme.js) and the variant's own
 *   photo moves to the front.
 */
(function () {
  'use strict';

  // A photo with no alt text group always shows. A grouped photo stays when its group value
  // matches the variant's value for that option, or when it is the variant's featured media.
  function isFiltered(groupName, groupValue, mediaId, productOptions, variant) {
    if (!groupName || !variant) {
      return false;
    }

    var optionIndex = -1;

    for (var i = 0; i < productOptions.length; i++) {
      if (String(productOptions[i]).toLowerCase() === groupName) {
        optionIndex = i;
        break;
      }
    }

    if (optionIndex === -1) {
      return false;
    }

    var variantValue = String(variant['option' + (optionIndex + 1)] || '').toLowerCase();
    var featuredId = variant.featured_media ? String(variant.featured_media.id) : null;

    return !(variantValue === groupValue || String(mediaId) === featuredId);
  }

  if (typeof module === 'object' && module.exports) {
    module.exports = { isFiltered: isFiltered };
  }

  if (typeof document === 'undefined') {
    return;
  }

  function initGallery(root) {
    if (root.hasAttribute('data-dg-ready')) {
      return;
    }

    root.setAttribute('data-dg-ready', '');

    var track = root.querySelector('[data-dg-track]');
    var thumbs = root.querySelector('[data-dg-thumbs]');
    var viewer = root.querySelector('[data-dg-viewer]');
    var viewerTrack = root.querySelector('[data-dg-viewer-track]');
    var viewerThumbs = root.querySelector('[data-dg-viewer-thumbs]');
    var viewerThumbsList = root.querySelector('[data-dg-viewer-thumbs-list]');
    var counter = root.querySelector('[data-dg-count]');
    var section = root.closest('[data-section-type="product"]');
    var productOptions = [];
    var lastFocus = null;
    var viewerIndex = 0;

    try {
      productOptions = JSON.parse(section.getAttribute('data-section-settings')).productOptions || [];
    } catch (error) {
      productOptions = [];
    }

    function visibleIn(list, selector) {
      if (!list) {
        return [];
      }

      return Array.prototype.filter.call(list.querySelectorAll(selector), function (element) {
        return !element.classList.contains('is-filtered');
      });
    }

    /* ---------- Swipe row and thumbnails ---------- */

    function setActiveThumb(mediaId) {
      if (!thumbs) {
        return;
      }

      var active = null;

      Array.prototype.forEach.call(thumbs.querySelectorAll('[data-dg-thumb]'), function (thumb) {
        var isActive = thumb.getAttribute('data-media-id') === mediaId;
        thumb.classList.toggle('is-active', isActive);

        if (isActive) {
          active = thumb;
        }
      });

      // Scroll the strip itself, never the page
      if (active && thumbs.scrollWidth > thumbs.clientWidth) {
        var left = active.offsetLeft - (thumbs.clientWidth - active.offsetWidth) / 2;
        thumbs.scrollTo({ left: Math.max(0, left), behavior: 'smooth' });
      }
    }

    function itemInView() {
      var closest = null;
      var closestDistance = Infinity;

      visibleIn(track, '[data-dg-item]').forEach(function (item) {
        var distance = Math.abs(item.offsetLeft - track.scrollLeft);

        if (distance < closestDistance) {
          closestDistance = distance;
          closest = item;
        }
      });

      return closest;
    }

    var trackFrame = null;

    track.addEventListener('scroll', function () {
      if (trackFrame) {
        return;
      }

      trackFrame = requestAnimationFrame(function () {
        trackFrame = null;
        var item = itemInView();

        if (item) {
          setActiveThumb(item.getAttribute('data-media-id'));
        }
      });
    }, { passive: true });

    if (thumbs) {
      thumbs.addEventListener('click', function (event) {
        var thumb = event.target.closest('[data-dg-thumb]');

        if (!thumb) {
          return;
        }

        var mediaId = thumb.getAttribute('data-media-id');
        var item = track.querySelector('[data-dg-item][data-media-id="' + mediaId + '"]');

        if (item) {
          // On phones the row snaps to the page gutter (scroll-padding), so land the photo there
          var gutter = parseFloat(getComputedStyle(track).scrollPaddingLeft) || 0;
          track.scrollTo({ left: Math.max(0, item.offsetLeft - gutter), behavior: 'smooth' });
        }

        setActiveThumb(mediaId);
      });
    }

    /* ---------- Fullscreen viewer ---------- */

    function currentSlideIndex() {
      var width = viewerTrack.clientWidth;
      return width ? Math.round(viewerTrack.scrollLeft / width) : 0;
    }

    function setActiveViewerThumb(index) {
      if (!viewerThumbsList) {
        return;
      }

      var slides = visibleIn(viewerTrack, '.dg__slide');
      var mediaId = slides[index] ? slides[index].getAttribute('data-media-id') : null;
      var active = null;

      Array.prototype.forEach.call(viewerThumbsList.querySelectorAll('[data-dg-viewer-thumb]'), function (thumb) {
        var isActive = thumb.getAttribute('data-media-id') === mediaId;
        thumb.classList.toggle('is-active', isActive);

        if (isActive) {
          active = thumb;
        }
      });

      // Keep the active thumbnail inside the strip when there are more than fit
      if (active && viewerThumbs.scrollWidth > viewerThumbs.clientWidth) {
        var left = active.offsetLeft - (viewerThumbs.clientWidth - active.offsetWidth) / 2;
        viewerThumbs.scrollTo({ left: Math.max(0, left), behavior: 'smooth' });
      }
    }

    // The visible counter was replaced by the thumbnail strip; the text stays for screen readers
    function updateCounter() {
      var total = visibleIn(viewerTrack, '.dg__slide').length;
      viewerIndex = Math.min(currentSlideIndex(), Math.max(total - 1, 0));
      counter.textContent = (viewerIndex + 1) + ' / ' + total;
      setActiveViewerThumb(viewerIndex);
    }

    function goTo(index, smooth) {
      var total = visibleIn(viewerTrack, '.dg__slide').length;

      resetZoom(false);

      if (!total) {
        return;
      }

      // Wrapping from the last photo to the first jumps rather than scrolling back past them all
      var wrapped = index < 0 || index >= total;
      var target = (index + total) % total;

      viewerTrack.scrollTo({
        left: target * viewerTrack.clientWidth,
        behavior: smooth && !wrapped ? 'smooth' : 'auto'
      });

      viewerIndex = target;

      // Highlight straight away instead of waiting for the scroll to finish
      setActiveViewerThumb(target);
    }

    function openViewer(mediaId) {
      var slides = visibleIn(viewerTrack, '.dg__slide');
      var index = 0;

      slides.forEach(function (slide, slideIndex) {
        if (slide.getAttribute('data-media-id') === mediaId) {
          index = slideIndex;
        }
      });

      lastFocus = document.activeElement;
      viewer.classList.toggle('dg__viewer--single', slides.length < 2);
      document.documentElement.classList.add('dg-lock');
      viewer.showModal();
      goTo(index, false);
      updateCounter();
    }

    track.addEventListener('click', function (event) {
      var button = event.target.closest('[data-dg-open]');

      if (!button) {
        return;
      }

      openViewer(button.closest('[data-dg-item]').getAttribute('data-media-id'));
    });

    viewer.addEventListener('click', function (event) {
      if (event.target.closest('[data-dg-close]')) {
        viewer.close();
      } else if (event.target.closest('[data-dg-prev]')) {
        goTo(currentSlideIndex() - 1, true);
      } else if (event.target.closest('[data-dg-next]')) {
        goTo(currentSlideIndex() + 1, true);
      } else if (event.target.closest('[data-dg-viewer-thumb]')) {
        var mediaId = event.target.closest('[data-dg-viewer-thumb]').getAttribute('data-media-id');
        var slides = visibleIn(viewerTrack, '.dg__slide');

        for (var i = 0; i < slides.length; i++) {
          if (slides[i].getAttribute('data-media-id') === mediaId) {
            goTo(i, true);
            break;
          }
        }
      } else if (event.target.classList.contains('dg__slide')) {
        // The dark space around a photo closes the viewer, the photo itself does not
        viewer.close();
      }
    });

    viewer.addEventListener('keydown', function (event) {
      // Esc is handled here rather than left to the browser: on live (2026-09-17) a real Escape
      // press did not close the modal dialog on its own, while this listener did receive keys
      if (event.key === 'Escape') {
        event.preventDefault();
        viewer.close();
      } else if (event.key === 'ArrowRight') {
        event.preventDefault();
        goTo(currentSlideIndex() + 1, true);
      } else if (event.key === 'ArrowLeft') {
        event.preventDefault();
        goTo(currentSlideIndex() - 1, true);
      }
    });

    viewer.addEventListener('close', function () {
      resetZoom(false);
      document.documentElement.classList.remove('dg-lock');

      if (lastFocus && typeof lastFocus.focus === 'function') {
        lastFocus.focus({ preventScroll: true });
      }
    });

    var counterFrame = null;

    viewerTrack.addEventListener('scroll', function () {
      if (counterFrame) {
        return;
      }

      counterFrame = requestAnimationFrame(function () {
        counterFrame = null;
        updateCounter();
      });
    }, { passive: true });

    // A rotated phone changes the slide width: stay on the same photo (goTo also drops any zoom)
    window.addEventListener('resize', function () {
      if (viewer.open) {
        goTo(viewerIndex, false);
      }
    });

    /* ---------- Zoom inside the viewer ---------- */

    // Touch: double-tap zooms in or out, two fingers pinch, one finger drags a zoomed photo.
    // Mouse: a click zooms in or out at the cursor, dragging moves a zoomed photo.
    // While a photo is zoomed the track stops scrolling (CSS .is-zoomed), so a drag never changes photo.
    var TAP_SCALE = 2.5;
    var MAX_SCALE = 3;
    var zoom = { img: null, scale: 1, x: 0, y: 0 };

    function applyZoom(animate) {
      if (!zoom.img) {
        return;
      }

      zoom.img.classList.toggle('is-animating', Boolean(animate));
      zoom.img.style.transform = zoom.scale > 1
        ? 'translate3d(' + zoom.x + 'px, ' + zoom.y + 'px, 0) scale(' + zoom.scale + ')'
        : '';
      viewer.classList.toggle('is-zoomed', zoom.scale > 1);
    }

    function resetZoom(animate) {
      if (!zoom.img) {
        return;
      }

      zoom.scale = 1;
      zoom.x = 0;
      zoom.y = 0;
      applyZoom(animate);
      zoom.img = null;
    }

    // The photo's box without the zoom transform (transform-origin is its top left corner)
    function baseBox(img) {
      var rect = img.getBoundingClientRect();
      var own = zoom.img === img;
      var scale = own ? zoom.scale : 1;

      return {
        left: rect.left - (own ? zoom.x : 0),
        top: rect.top - (own ? zoom.y : 0),
        width: rect.width / scale,
        height: rect.height / scale
      };
    }

    // While the zoomed photo still fits the stage along an axis it stays centred on its own centre;
    // once it is bigger, it may move but never pull an edge inside the stage
    function clampOffset(offset, size, scale, baseStart, stageStart, stageSize) {
      var scaled = size * scale;

      if (scaled <= stageSize) {
        return (size - scaled) / 2;
      }

      return Math.min(stageStart - baseStart, Math.max(stageStart + stageSize - scaled - baseStart, offset));
    }

    function zoomAt(img, clientX, clientY, scale, animate) {
      if (zoom.img && zoom.img !== img) {
        resetZoom(false);
      }

      var box = baseBox(img);
      var rect = img.getBoundingClientRect();
      var currentScale = zoom.img === img ? zoom.scale : 1;
      var stage = img.closest('.dg__slide').getBoundingClientRect();

      // The point of the photo under the finger or cursor, in unzoomed pixels, stays where it is
      var pointX = (clientX - rect.left) / currentScale;
      var pointY = (clientY - rect.top) / currentScale;

      scale = Math.min(MAX_SCALE, Math.max(1, scale));

      zoom.img = img;
      zoom.scale = scale;
      zoom.x = clampOffset(clientX - box.left - scale * pointX, box.width, scale, box.left, stage.left, stage.width);
      zoom.y = clampOffset(clientY - box.top - scale * pointY, box.height, scale, box.top, stage.top, stage.height);

      // Ask once for a sharper file: srcset goes up to 2400px, sizes at the largest zoom lets the browser pick it
      if (scale > 1 && !img.hasAttribute('data-dg-zoom-sizes')) {
        img.setAttribute('data-dg-zoom-sizes', img.getAttribute('sizes') || '');
        img.setAttribute('sizes', (MAX_SCALE * 100) + 'vw');
      }

      applyZoom(animate);
    }

    function panTo(x, y) {
      var img = zoom.img;
      var box = baseBox(img);
      var stage = img.closest('.dg__slide').getBoundingClientRect();

      zoom.x = clampOffset(x, box.width, zoom.scale, box.left, stage.left, stage.width);
      zoom.y = clampOffset(y, box.height, zoom.scale, box.top, stage.top, stage.height);
      applyZoom(false);
    }

    function toggleZoom(img, clientX, clientY) {
      if (zoom.img === img && zoom.scale > 1) {
        resetZoom(true);
      } else {
        zoomAt(img, clientX, clientY, TAP_SCALE, true);
      }
    }

    function touchDistance(touches) {
      return Math.hypot(touches[0].clientX - touches[1].clientX, touches[0].clientY - touches[1].clientY);
    }

    var touch = null;
    var lastTap = null;

    viewerTrack.addEventListener('touchstart', function (event) {
      var slide = event.target.closest('.dg__slide');
      var img = slide && slide.querySelector('.dg__slide-img');

      if (!img) {
        touch = null;
        return;
      }

      if (event.touches.length === 2) {
        touch = {
          type: 'pinch',
          img: img,
          distance: touchDistance(event.touches),
          scale: zoom.img === img ? zoom.scale : 1
        };
      } else if (event.touches.length === 1) {
        touch = {
          type: zoom.img === img && zoom.scale > 1 ? 'pan' : 'tap',
          img: img,
          startX: event.touches[0].clientX,
          startY: event.touches[0].clientY,
          originX: zoom.x,
          originY: zoom.y,
          time: Date.now(),
          moved: false,
          onPhoto: event.target === img
        };
      }
    }, { passive: true });

    // Not passive: a pinch and a drag of a zoomed photo cancel the browser's own scroll and page zoom
    viewerTrack.addEventListener('touchmove', function (event) {
      if (!touch) {
        return;
      }

      if (touch.type === 'pinch') {
        if (event.touches.length === 2) {
          if (event.cancelable) {
            event.preventDefault();
          }

          zoomAt(
            touch.img,
            (event.touches[0].clientX + event.touches[1].clientX) / 2,
            (event.touches[0].clientY + event.touches[1].clientY) / 2,
            touch.scale * touchDistance(event.touches) / touch.distance,
            false
          );
        }

        return;
      }

      var dx = event.touches[0].clientX - touch.startX;
      var dy = event.touches[0].clientY - touch.startY;

      if (Math.abs(dx) > 8 || Math.abs(dy) > 8) {
        touch.moved = true;
      }

      if (touch.type === 'pan') {
        if (event.cancelable) {
          event.preventDefault();
        }

        panTo(touch.originX + dx, touch.originY + dy);
      }
    }, { passive: false });

    viewerTrack.addEventListener('touchend', function (event) {
      if (!touch) {
        return;
      }

      if (touch.type === 'pinch') {
        if (event.touches.length === 1 && zoom.img === touch.img && zoom.scale > 1) {
          // The finger still down carries on dragging
          touch = {
            type: 'pan',
            img: touch.img,
            startX: event.touches[0].clientX,
            startY: event.touches[0].clientY,
            originX: zoom.x,
            originY: zoom.y,
            time: 0,
            moved: true,
            onPhoto: false
          };
        } else if (!event.touches.length) {
          touch = null;
        }

        return;
      }

      if (event.touches.length) {
        return;
      }

      var ended = touch;
      var now = Date.now();
      touch = null;

      if (ended.moved || !ended.onPhoto || now - ended.time > 300) {
        lastTap = null;
        return;
      }

      if (lastTap && lastTap.img === ended.img && now - lastTap.time < 300 &&
          Math.abs(ended.startX - lastTap.x) < 30 && Math.abs(ended.startY - lastTap.y) < 30) {
        lastTap = null;

        // No click after the second tap
        if (event.cancelable) {
          event.preventDefault();
        }

        toggleZoom(ended.img, ended.startX, ended.startY);
      } else {
        lastTap = { img: ended.img, time: now, x: ended.startX, y: ended.startY };
      }
    });

    viewerTrack.addEventListener('touchcancel', function () {
      touch = null;
    });

    var drag = null;

    viewerTrack.addEventListener('pointerdown', function (event) {
      var img = event.target.closest('.dg__slide-img');

      if (event.pointerType !== 'mouse' || event.button !== 0 || !img) {
        return;
      }

      drag = {
        img: img,
        pointerId: event.pointerId,
        startX: event.clientX,
        startY: event.clientY,
        originX: zoom.x,
        originY: zoom.y,
        zoomed: zoom.img === img && zoom.scale > 1,
        moved: false
      };

      if (drag.zoomed) {
        img.setPointerCapture(event.pointerId);
      }

      // No native image drag or text selection
      event.preventDefault();
    });

    viewerTrack.addEventListener('pointermove', function (event) {
      if (!drag || event.pointerId !== drag.pointerId) {
        return;
      }

      var dx = event.clientX - drag.startX;
      var dy = event.clientY - drag.startY;

      if (Math.abs(dx) > 3 || Math.abs(dy) > 3) {
        drag.moved = true;
      }

      if (drag.zoomed && drag.moved) {
        panTo(drag.originX + dx, drag.originY + dy);
      }
    });

    viewerTrack.addEventListener('pointerup', function (event) {
      if (!drag || event.pointerId !== drag.pointerId) {
        return;
      }

      var ended = drag;
      drag = null;

      if (!ended.moved) {
        toggleZoom(ended.img, event.clientX, event.clientY);
      }
    });

    viewerTrack.addEventListener('pointercancel', function () {
      drag = null;
    });

    /* ---------- Variant changes ---------- */

    function moveToFront(mediaId) {
      [track, thumbs, viewerTrack, viewerThumbsList].forEach(function (list) {
        if (!list) {
          return;
        }

        var element = list.querySelector('[data-media-id="' + mediaId + '"]');

        if (element && element !== list.firstElementChild) {
          list.insertBefore(element, list.firstElementChild);
        }
      });
    }

    if (section) {
      section.addEventListener('variant:changed', function (event) {
        var variant = event.detail && event.detail.variant;

        if (!variant) {
          return;
        }

        [track, thumbs, viewerTrack, viewerThumbsList].forEach(function (list) {
          if (!list) {
            return;
          }

          Array.prototype.forEach.call(list.querySelectorAll('[data-media-id]'), function (element) {
            element.classList.toggle('is-filtered', isFiltered(
              element.getAttribute('data-group-name'),
              element.getAttribute('data-group-value'),
              element.getAttribute('data-media-id'),
              productOptions,
              variant
            ));
          });
        });

        if (variant.featured_media) {
          moveToFront(String(variant.featured_media.id));
        }

        track.scrollTo({ left: 0, behavior: 'auto' });

        var first = visibleIn(track, '[data-dg-item]')[0];

        if (first) {
          setActiveThumb(first.getAttribute('data-media-id'));
        }
      });
    }
  }

  function initAll(scope) {
    Array.prototype.forEach.call((scope || document).querySelectorAll('[data-disney-gallery]'), initGallery);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      initAll();
    });
  } else {
    initAll();
  }

  // Theme editor re-renders the section without re-running this file
  document.addEventListener('shopify:section:load', function (event) {
    initAll(event.target);
  });
})();
